/**
 * Illustrative data, one fixture per widget type — **shaped as the v2 contract
 * returns it** (CT-5641).
 *
 * ## Why these are not hand-written any more
 *
 * Every catalogue widget takes its `data` as a prop and renders nothing
 * without it. Three surfaces need to draw a widget before any data exists —
 * the builder's live preview, the library's detail panel, and Storybook — and
 * each one inventing its own fixture is how the same Donut ends up looking
 * like three different components.
 *
 * They used to be invented here too, in whatever shape each component already
 * happened to read. That was fine while the component's shape was the only
 * shape there was. It stopped being fine when the catalog started publishing
 * an `output_schema` per type: a fixture that disagrees with the contract
 * teaches the component to read something the backend will never send, and
 * the disagreement only surfaces the day real data arrives.
 *
 * So each fixture below is the type's own `example.output.json` → `data`, as
 * published in the v2 proposal: same keys, same nesting, same numbers. The
 * shape and the contract cannot drift, because they are the same document.
 *
 * ## The one thing that is not verbatim
 *
 * The proposal's examples are written in Spanish («Ventas», «Técnico»,
 * «Vehículos»). The display text — `label`, `caption`, `title`, `formula` and
 * the handful like them — is in English here instead, because this is what the
 * builder's live preview, the library's thumbnails and Storybook show, and the
 * product is English today.
 *
 * Only those values changed. Every key, identifier, code, uuid, date and
 * number is byte-for-byte the contract's, which is what the shape is being
 * checked against in `catalogWidgets.test.jsx`.
 *
 * ## What it is not
 *
 * It is not product copy and it is not translated at runtime: these stand in
 * for a backend payload, and running them through i18n would imply a user ever
 * sees them in their own language. A caller with real rows passes `data` and
 * none of this loads.
 */

const SAMPLES_BY_TYPE = {
    "kpi": {
    "value": 120000,
    "label": "Label",
    "format": {
      "style": "CURRENCY",
      "currency_code": "USD"
    },
    "caption": "Caption",
    "comparison_value": 100000,
    "delta": 20000,
    "delta_pct": 0.2,
    "trend": "UP",
    "target_value": 150000,
    "target_pct": 0.8,
    "color_token": "NEUTRAL",
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "comparisons": [
      {
        "key": "comparison_1",
        "label": "Comparison 1",
        "mode": "PREVIOUS_PERIOD",
        "compare_as": "DELTA",
        "period": {
          "grain": "MONTH",
          "start": "2026-09-01",
          "end": "2026-09-30",
          "label": "Previous period"
        },
        "value": 100000,
        "delta": 20000,
        "delta_pct": 0.2,
        "trend": "UP"
      },
      {
        "key": "comparison_2",
        "label": "Comparison 2",
        "mode": "PERIOD_TO_DATE",
        "compare_as": "SHARE",
        "period": {
          "grain": "YEAR",
          "start": "2026-01-01",
          "end": "2026-10-31",
          "label": "To date"
        },
        "value": 950000,
        "share": 0.1263
      }
    ]
  },
    "metric_row": {
    "items": [
      {
        "key": "metric_1",
        "label": "Metric 1",
        "value": 126000,
        "format": {
          "style": "INTEGER",
          "compact": true
        },
        "trend": "UP",
        "delta_pct": 0.2,
        "comparison_value": 105000,
        "comparison_label": "Prev"
      },
      {
        "key": "metric_2",
        "label": "Metric 2",
        "value": 412,
        "format": {
          "style": "INTEGER"
        },
        "trend": "UP",
        "delta_pct": 0.035,
        "comparison_value": 398,
        "comparison_label": "Prev"
      },
      {
        "key": "metric_3",
        "label": "Metric 3",
        "value": 305.83,
        "format": {
          "style": "DECIMAL",
          "decimals": 2
        },
        "trend": null,
        "delta_pct": null,
        "comparison_value": null
      },
      {
        "key": "metric_4",
        "label": "Metric 4",
        "value": 3.4,
        "format": {
          "style": "DECIMAL",
          "decimals": 1
        },
        "trend": "DOWN",
        "delta_pct": 0.097,
        "comparison_value": 3.1,
        "comparison_label": "Prev"
      }
    ],
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    }
  },
    "indicator": {
    "value": 4.32,
    "min": 0,
    "max": 5,
    "target": 4.5,
    "ratio": 0.864,
    "format": {
      "style": "DECIMAL",
      "decimals": 2
    },
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "color_token": "WARNING",
    "thresholds": [
      {
        "operator": "LT",
        "value": 4,
        "color_token": "DANGER"
      },
      {
        "operator": "BETWEEN",
        "value": 4,
        "value_to": 4.5,
        "color_token": "WARNING"
      },
      {
        "operator": "GTE",
        "value": 4.5,
        "color_token": "SUCCESS"
      }
    ]
  },
    "cost_kpi": {
    "value": 43870,
    "currency_code": "USD",
    "format": {
      "style": "CURRENCY",
      "currency_code": "USD"
    },
    "label": "Label",
    "caption": "Caption",
    "budget_value": 50000,
    "budget_used_pct": 0.8774,
    "comparison_value": 39200,
    "delta_pct": 0.1191,
    "trend": "UP",
    "color_token": "WARNING",
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "comparisons": [
      {
        "key": "comparison_1",
        "label": "Comparison 1",
        "mode": "PREVIOUS_PERIOD",
        "compare_as": "DELTA",
        "period": {
          "grain": "MONTH",
          "start": "2026-09-01",
          "end": "2026-09-30",
          "label": "Previous period"
        },
        "value": 39200,
        "delta": 4670,
        "delta_pct": 0.1191,
        "trend": "UP"
      }
    ]
  },
    "composite_metric": {
    "value": 0.724,
    "format": {
      "style": "PERCENT",
      "decimals": 1
    },
    "formula": "part_1 / part_2",
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "components": [
      {
        "label": "Part 1",
        "value": 184,
        "alias": "part_1"
      },
      {
        "label": "Part 2",
        "value": 254,
        "alias": "part_2"
      }
    ],
    "color_token": "SUCCESS"
  },
    "line_chart": {
    "x_axis": {
      "type": "TIME",
      "grain": "MONTH",
      "field_key": "x",
      "label": "Period"
    },
    "y_axes": [
      {
        "key": "LEFT",
        "label": "Value",
        "format": {
          "style": "INTEGER",
          "compact": true
        }
      }
    ],
    "period": {
      "grain": "MONTH",
      "start": "2026-05-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "series": [
      {
        "key": "series_1",
        "label": "Series 1",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_1",
        "points": [
          {
            "x": "2026-05-01",
            "y": 41000
          },
          {
            "x": "2026-06-01",
            "y": 44000
          },
          {
            "x": "2026-07-01",
            "y": 43000
          },
          {
            "x": "2026-08-01",
            "y": 47000
          },
          {
            "x": "2026-09-01",
            "y": 46000
          },
          {
            "x": "2026-10-01",
            "y": 52000
          }
        ]
      },
      {
        "key": "series_2",
        "label": "Series 2",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_2",
        "points": [
          {
            "x": "2026-05-01",
            "y": 31000
          },
          {
            "x": "2026-06-01",
            "y": 33000
          },
          {
            "x": "2026-07-01",
            "y": 34000
          },
          {
            "x": "2026-08-01",
            "y": 33000
          },
          {
            "x": "2026-09-01",
            "y": 35000
          },
          {
            "x": "2026-10-01",
            "y": 36000
          }
        ]
      },
      {
        "key": "series_3",
        "label": "Series 3",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_3",
        "points": [
          {
            "x": "2026-05-01",
            "y": 21000
          },
          {
            "x": "2026-06-01",
            "y": 22000
          },
          {
            "x": "2026-07-01",
            "y": 24000
          },
          {
            "x": "2026-08-01",
            "y": 23000
          },
          {
            "x": "2026-09-01",
            "y": 25000
          },
          {
            "x": "2026-10-01",
            "y": 27000
          }
        ]
      },
      {
        "key": "__others__",
        "label": "Others",
        "y_axis": "LEFT",
        "is_others": true,
        "color_token": "NEUTRAL",
        "points": [
          {
            "x": "2026-05-01",
            "y": 9000
          },
          {
            "x": "2026-06-01",
            "y": 9500
          },
          {
            "x": "2026-07-01",
            "y": 10000
          },
          {
            "x": "2026-08-01",
            "y": 9800
          },
          {
            "x": "2026-09-01",
            "y": 10500
          },
          {
            "x": "2026-10-01",
            "y": 11000
          }
        ]
      }
    ]
  },
    "bar_chart": {
    "x_axis": {
      "type": "TIME",
      "grain": "MONTH",
      "field_key": "x",
      "label": "Period"
    },
    "y_axes": [
      {
        "key": "LEFT",
        "label": "Value",
        "format": {
          "style": "INTEGER",
          "compact": true
        }
      }
    ],
    "series": [
      {
        "key": "series_1",
        "label": "Series 1",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_1",
        "points": [
          {
            "x": "2026-08-01",
            "y": 41000
          },
          {
            "x": "2026-09-01",
            "y": 38000
          },
          {
            "x": "2026-10-01",
            "y": 45000
          }
        ]
      },
      {
        "key": "series_2",
        "label": "Series 2",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_2",
        "points": [
          {
            "x": "2026-08-01",
            "y": 30000
          },
          {
            "x": "2026-09-01",
            "y": 0
          },
          {
            "x": "2026-10-01",
            "y": 33000
          }
        ]
      },
      {
        "key": "__others__",
        "label": "Others",
        "y_axis": "LEFT",
        "is_others": true,
        "color_token": "NEUTRAL",
        "points": [
          {
            "x": "2026-08-01",
            "y": 12000
          },
          {
            "x": "2026-09-01",
            "y": 9000
          },
          {
            "x": "2026-10-01",
            "y": 15000
          }
        ]
      }
    ]
  },
    "area_chart": {
    "x_axis": {
      "type": "TIME",
      "grain": "WEEK",
      "field_key": "x",
      "label": "Period"
    },
    "y_axes": [
      {
        "key": "LEFT",
        "label": "Value",
        "format": {
          "style": "INTEGER"
        }
      }
    ],
    "period": {
      "grain": "WEEK",
      "start": "2026-08-17",
      "end": "2026-10-11",
      "label": "Period label"
    },
    "series": [
      {
        "key": "series_1",
        "label": "Series 1",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_1",
        "points": [
          {
            "x": "2026-08-17",
            "y": 210
          },
          {
            "x": "2026-08-24",
            "y": 225
          },
          {
            "x": "2026-08-31",
            "y": 198
          },
          {
            "x": "2026-09-07",
            "y": 232
          },
          {
            "x": "2026-09-14",
            "y": 240
          },
          {
            "x": "2026-09-21",
            "y": 228
          },
          {
            "x": "2026-09-28",
            "y": 251
          },
          {
            "x": "2026-10-05",
            "y": 262
          }
        ]
      },
      {
        "key": "series_2",
        "label": "Series 2",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_2",
        "points": [
          {
            "x": "2026-08-17",
            "y": 96
          },
          {
            "x": "2026-08-24",
            "y": 88
          },
          {
            "x": "2026-08-31",
            "y": 104
          },
          {
            "x": "2026-09-07",
            "y": 92
          },
          {
            "x": "2026-09-14",
            "y": 110
          },
          {
            "x": "2026-09-21",
            "y": 118
          },
          {
            "x": "2026-09-28",
            "y": 102
          },
          {
            "x": "2026-10-05",
            "y": 121
          }
        ]
      },
      {
        "key": "series_3",
        "label": "Series 3",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_3",
        "points": [
          {
            "x": "2026-08-17",
            "y": 64
          },
          {
            "x": "2026-08-24",
            "y": 71
          },
          {
            "x": "2026-08-31",
            "y": 58
          },
          {
            "x": "2026-09-07",
            "y": 80
          },
          {
            "x": "2026-09-14",
            "y": 76
          },
          {
            "x": "2026-09-21",
            "y": 69
          },
          {
            "x": "2026-09-28",
            "y": 88
          },
          {
            "x": "2026-10-05",
            "y": 84
          }
        ]
      },
      {
        "key": "__others__",
        "label": "Others",
        "y_axis": "LEFT",
        "is_others": true,
        "color_token": "NEUTRAL",
        "points": [
          {
            "x": "2026-08-17",
            "y": 30
          },
          {
            "x": "2026-08-24",
            "y": 26
          },
          {
            "x": "2026-08-31",
            "y": 34
          },
          {
            "x": "2026-09-07",
            "y": 28
          },
          {
            "x": "2026-09-14",
            "y": 31
          },
          {
            "x": "2026-09-21",
            "y": 36
          },
          {
            "x": "2026-09-28",
            "y": 29
          },
          {
            "x": "2026-10-05",
            "y": 33
          }
        ]
      }
    ]
  },
    "stacked_bar": {
    "x_axis": {
      "type": "TIME",
      "grain": "MONTH",
      "field_key": "x",
      "label": "Period"
    },
    "y_axes": [
      {
        "key": "LEFT",
        "label": "Value",
        "format": {
          "style": "INTEGER",
          "compact": true
        }
      }
    ],
    "period": {
      "grain": "MONTH",
      "start": "2026-05-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "series": [
      {
        "key": "series_1",
        "label": "Series 1",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_1",
        "points": [
          {
            "x": "2026-05-01",
            "y": 24000
          },
          {
            "x": "2026-06-01",
            "y": 24500
          },
          {
            "x": "2026-07-01",
            "y": 25500
          },
          {
            "x": "2026-08-01",
            "y": 27000
          },
          {
            "x": "2026-09-01",
            "y": 27500
          },
          {
            "x": "2026-10-01",
            "y": 30500
          }
        ]
      },
      {
        "key": "series_2",
        "label": "Series 2",
        "y_axis": "LEFT",
        "color_token": "CATEGORICAL_2",
        "points": [
          {
            "x": "2026-05-01",
            "y": 14000
          },
          {
            "x": "2026-06-01",
            "y": 14500
          },
          {
            "x": "2026-07-01",
            "y": 14800
          },
          {
            "x": "2026-08-01",
            "y": 16000
          },
          {
            "x": "2026-09-01",
            "y": 15400
          },
          {
            "x": "2026-10-01",
            "y": 17000
          }
        ]
      },
      {
        "key": "__others__",
        "label": "Others",
        "y_axis": "LEFT",
        "is_others": true,
        "color_token": "NEUTRAL",
        "points": [
          {
            "x": "2026-05-01",
            "y": 3200
          },
          {
            "x": "2026-06-01",
            "y": 3200
          },
          {
            "x": "2026-07-01",
            "y": 3500
          },
          {
            "x": "2026-08-01",
            "y": 3300
          },
          {
            "x": "2026-09-01",
            "y": 3200
          },
          {
            "x": "2026-10-01",
            "y": 3500
          }
        ]
      }
    ]
  },
    "sparkline": {
    "points": [
      {
        "x": "2026-07-20",
        "y": 88
      },
      {
        "x": "2026-07-27",
        "y": 94
      },
      {
        "x": "2026-08-03",
        "y": 91
      },
      {
        "x": "2026-08-10",
        "y": 102
      },
      {
        "x": "2026-08-17",
        "y": 97
      },
      {
        "x": "2026-08-24",
        "y": 106
      },
      {
        "x": "2026-08-31",
        "y": 99
      },
      {
        "x": "2026-09-07",
        "y": 114
      },
      {
        "x": "2026-09-14",
        "y": 108
      },
      {
        "x": "2026-09-21",
        "y": 108
      },
      {
        "x": "2026-09-28",
        "y": 121
      },
      {
        "x": "2026-10-05",
        "y": 118
      }
    ],
    "min": 88,
    "max": 121,
    "summary_value": 118,
    "delta_pct": -0.0248,
    "format": {
      "style": "INTEGER"
    },
    "period": {
      "grain": "WEEK",
      "start": "2026-07-20",
      "end": "2026-10-11",
      "label": "Period label"
    }
  },
    "pie_chart": {
    "total": 128000,
    "format": {
      "style": "INTEGER",
      "compact": true
    },
    "totalLabel": "total",
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "slices": [
      {
        "key": "slice_1",
        "label": "Slice 1",
        "value": 58000,
        "pct": 0.46,
        "color_token": "CATEGORICAL_1"
      },
      {
        "key": "slice_2",
        "label": "Slice 2",
        "value": 37800,
        "pct": 0.3,
        "color_token": "CATEGORICAL_2"
      },
      {
        "key": "slice_3",
        "label": "Slice 3",
        "value": 21400,
        "pct": 0.17,
        "color_token": "CATEGORICAL_3"
      },
      {
        "key": "__others__",
        "label": "Others",
        "value": 8800,
        "pct": 0.07,
        "is_others": true,
        "color_token": "NEUTRAL"
      }
    ]
  },
    "donut": {
    "total": 1000,
    "format": {
      "style": "INTEGER"
    },
    "totalLabel": "total",
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "slices": [
      {
        "key": "slice_1",
        "label": "Slice 1",
        "value": 480,
        "pct": 0.48,
        "color_token": "CATEGORICAL_1"
      },
      {
        "key": "slice_2",
        "label": "Slice 2",
        "value": 300,
        "pct": 0.3,
        "color_token": "CATEGORICAL_2"
      },
      {
        "key": "slice_3",
        "label": "Slice 3",
        "value": 150,
        "pct": 0.15,
        "color_token": "CATEGORICAL_3"
      },
      {
        "key": "__others__",
        "label": "Others",
        "value": 70,
        "pct": 0.07,
        "is_others": true,
        "color_token": "NEUTRAL"
      }
    ]
  },
    "spend_breakdown": {
    "total": 71500,
    "currency_code": "USD",
    "format": {
      "style": "CURRENCY",
      "currency_code": "USD"
    },
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "items": [
      {
        "key": "cat_1",
        "label": "Category 1",
        "value": 43870,
        "pct": 0.6136,
        "color_token": "CATEGORICAL_1",
        "children": [
          {
            "key": "sub_1",
            "label": "Sub 1",
            "value": 21400,
            "pct": 0.4879
          },
          {
            "key": "sub_2",
            "label": "Sub 2",
            "value": 12650,
            "pct": 0.2883
          },
          {
            "key": "__others__",
            "label": "Others",
            "value": 9820,
            "pct": 0.2238,
            "is_others": true
          }
        ]
      },
      {
        "key": "cat_2",
        "label": "Category 2",
        "value": 18200,
        "pct": 0.2545,
        "color_token": "CATEGORICAL_2",
        "children": []
      },
      {
        "key": "cat_3",
        "label": "Category 3",
        "value": 6500,
        "pct": 0.0909,
        "color_token": "CATEGORICAL_3",
        "children": []
      },
      {
        "key": "__others__",
        "label": "Others",
        "value": 2930,
        "pct": 0.041,
        "is_others": true,
        "color_token": "NEUTRAL",
        "children": []
      }
    ]
  },
    "funnel": {
    "format": {
      "style": "INTEGER"
    },
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "stages": [
      {
        "key": "stage_1",
        "label": "Stage 1",
        "value": 412,
        "pct_of_first": 1,
        "pct_of_previous": null
      },
      {
        "key": "stage_2",
        "label": "Stage 2",
        "value": 371,
        "pct_of_first": 0.9005,
        "pct_of_previous": 0.9005
      },
      {
        "key": "stage_3",
        "label": "Stage 3",
        "value": 254,
        "pct_of_first": 0.6165,
        "pct_of_previous": 0.6846
      },
      {
        "key": "stage_4",
        "label": "Stage 4",
        "value": 184,
        "pct_of_first": 0.4466,
        "pct_of_previous": 0.7244
      },
      {
        "key": "stage_5",
        "label": "Stage 5",
        "value": 161,
        "pct_of_first": 0.3908,
        "pct_of_previous": 0.875
      }
    ]
  },
    "heatmap": {
    "x_categories": [
      "C1",
      "C2",
      "C3",
      "C4",
      "C5",
      "C6",
      "C7",
      "C8",
      "C9",
      "C10"
    ],
    "y_categories": [
      "R1",
      "R2",
      "R3",
      "R4",
      "R5",
      "R6"
    ],
    "cells": [
      {
        "x": "C1",
        "y": "R1",
        "value": 9
      },
      {
        "x": "C2",
        "y": "R1",
        "value": 14
      },
      {
        "x": "C3",
        "y": "R1",
        "value": 14
      },
      {
        "x": "C4",
        "y": "R1",
        "value": 9
      },
      {
        "x": "C5",
        "y": "R1",
        "value": 6
      },
      {
        "x": "C6",
        "y": "R1",
        "value": 8
      },
      {
        "x": "C7",
        "y": "R1",
        "value": 10
      },
      {
        "x": "C8",
        "y": "R1",
        "value": 7
      },
      {
        "x": "C9",
        "y": "R1",
        "value": 3
      },
      {
        "x": "C10",
        "y": "R1",
        "value": 1
      },
      {
        "x": "C1",
        "y": "R2",
        "value": 7
      },
      {
        "x": "C2",
        "y": "R2",
        "value": 12
      },
      {
        "x": "C3",
        "y": "R2",
        "value": 12
      },
      {
        "x": "C4",
        "y": "R2",
        "value": 8
      },
      {
        "x": "C5",
        "y": "R2",
        "value": 5
      },
      {
        "x": "C6",
        "y": "R2",
        "value": 7
      },
      {
        "x": "C7",
        "y": "R2",
        "value": 8
      },
      {
        "x": "C8",
        "y": "R2",
        "value": 6
      },
      {
        "x": "C9",
        "y": "R2",
        "value": 3
      },
      {
        "x": "C10",
        "y": "R2",
        "value": 1
      },
      {
        "x": "C1",
        "y": "R3",
        "value": 8
      },
      {
        "x": "C2",
        "y": "R3",
        "value": 12
      },
      {
        "x": "C3",
        "y": "R3",
        "value": 12
      },
      {
        "x": "C4",
        "y": "R3",
        "value": 8
      },
      {
        "x": "C5",
        "y": "R3",
        "value": 6
      },
      {
        "x": "C6",
        "y": "R3",
        "value": 7
      },
      {
        "x": "C7",
        "y": "R3",
        "value": 9
      },
      {
        "x": "C8",
        "y": "R3",
        "value": 7
      },
      {
        "x": "C9",
        "y": "R3",
        "value": 3
      },
      {
        "x": "C10",
        "y": "R3",
        "value": 1
      },
      {
        "x": "C1",
        "y": "R4",
        "value": 8
      },
      {
        "x": "C2",
        "y": "R4",
        "value": 13
      },
      {
        "x": "C3",
        "y": "R4",
        "value": 13
      },
      {
        "x": "C4",
        "y": "R4",
        "value": 9
      },
      {
        "x": "C5",
        "y": "R4",
        "value": 6
      },
      {
        "x": "C6",
        "y": "R4",
        "value": 7
      },
      {
        "x": "C7",
        "y": "R4",
        "value": 9
      },
      {
        "x": "C8",
        "y": "R4",
        "value": 7
      },
      {
        "x": "C9",
        "y": "R4",
        "value": 3
      },
      {
        "x": "C10",
        "y": "R4",
        "value": 1
      },
      {
        "x": "C1",
        "y": "R5",
        "value": 10
      },
      {
        "x": "C2",
        "y": "R5",
        "value": 15
      },
      {
        "x": "C3",
        "y": "R5",
        "value": 16
      },
      {
        "x": "C4",
        "y": "R5",
        "value": 10
      },
      {
        "x": "C5",
        "y": "R5",
        "value": 7
      },
      {
        "x": "C6",
        "y": "R5",
        "value": 9
      },
      {
        "x": "C7",
        "y": "R5",
        "value": 11
      },
      {
        "x": "C8",
        "y": "R5",
        "value": 8
      },
      {
        "x": "C9",
        "y": "R5",
        "value": 4
      },
      {
        "x": "C10",
        "y": "R5",
        "value": 1
      },
      {
        "x": "C1",
        "y": "R6",
        "value": 5
      },
      {
        "x": "C2",
        "y": "R6",
        "value": 8
      },
      {
        "x": "C3",
        "y": "R6",
        "value": 8
      },
      {
        "x": "C4",
        "y": "R6",
        "value": 6
      },
      {
        "x": "C5",
        "y": "R6",
        "value": 4
      },
      {
        "x": "C6",
        "y": "R6",
        "value": 5
      },
      {
        "x": "C7",
        "y": "R6",
        "value": null
      },
      {
        "x": "C8",
        "y": "R6",
        "value": null
      },
      {
        "x": "C9",
        "y": "R6",
        "value": null
      },
      {
        "x": "C10",
        "y": "R6",
        "value": null
      }
    ],
    "min": 1,
    "max": 16,
    "format": {
      "style": "INTEGER"
    },
    "period": {
      "grain": "DAY",
      "start": "2026-09-09",
      "end": "2026-10-08",
      "label": "Period label"
    }
  },
    "usage_map": {
    "format": {
      "style": "INTEGER"
    },
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    },
    "cells": [
      {
        "key": "resource_1",
        "label": "Resource 1",
        "group": "Group 1",
        "value": 162,
        "limit": 176,
        "utilization_pct": 0.9205,
        "color_token": "DANGER"
      },
      {
        "key": "resource_2",
        "label": "Resource 2",
        "group": "Group 1",
        "value": 151,
        "limit": 176,
        "utilization_pct": 0.858,
        "color_token": "WARNING"
      },
      {
        "key": "resource_3",
        "label": "Resource 3",
        "group": "Group 1",
        "value": 118,
        "limit": 176,
        "utilization_pct": 0.6705,
        "color_token": null
      },
      {
        "key": "resource_4",
        "label": "Resource 4",
        "group": "Group 1",
        "value": 96,
        "limit": 176,
        "utilization_pct": 0.5455,
        "color_token": null
      },
      {
        "key": "resource_5",
        "label": "Resource 5",
        "group": "Group 1",
        "value": 171,
        "limit": 176,
        "utilization_pct": 0.9716,
        "color_token": "DANGER"
      },
      {
        "key": "resource_6",
        "label": "Resource 6",
        "group": "Group 1",
        "value": 88,
        "limit": 176,
        "utilization_pct": 0.5,
        "color_token": null
      },
      {
        "key": "resource_7",
        "label": "Resource 7",
        "group": "Group 1",
        "value": 104,
        "limit": 176,
        "utilization_pct": 0.5909,
        "color_token": null
      },
      {
        "key": "resource_8",
        "label": "Resource 8",
        "group": "Group 1",
        "value": 64,
        "limit": 176,
        "utilization_pct": 0.3636,
        "color_token": null
      }
    ]
  },
    "correlation": {
    "points": [
      {
        "x": 2.8,
        "y": 370,
        "size": 58,
        "label": "Point 1",
        "series": "Series 1"
      },
      {
        "x": 2.9,
        "y": 379,
        "size": 51,
        "label": "Point 2",
        "series": "Series 1"
      },
      {
        "x": 3,
        "y": 380,
        "size": 47,
        "label": "Point 3",
        "series": "Series 2"
      },
      {
        "x": 2.75,
        "y": 346,
        "size": 44,
        "label": "Point 4",
        "series": "Series 2"
      },
      {
        "x": 3.4,
        "y": 412,
        "size": 39,
        "label": "Point 5",
        "series": "Series 3"
      },
      {
        "x": 2.2,
        "y": 285,
        "size": 36,
        "label": "Point 6",
        "series": "Series 3"
      },
      {
        "x": 3.8,
        "y": 455,
        "size": 31,
        "label": "Point 7",
        "series": "Series 1"
      },
      {
        "x": 1.9,
        "y": 262,
        "size": 29,
        "label": "Point 8",
        "series": "Series 2"
      }
    ],
    "trendline": {
      "slope": 106.2,
      "intercept": 72.4
    },
    "coefficient": 0.97,
    "xLabel": "X value",
    "yLabel": "Y value",
    "yFormat": {
      "style": "INTEGER"
    },
    "xFormat": {
      "style": "DECIMAL",
      "decimals": 1
    }
  },
    "map": {
    "mode": "POINTS",
    "bounds": {
      "north": 4.76,
      "south": 4.61,
      "east": -74.01,
      "west": -74.16
    },
    "format": {
      "style": "INTEGER",
      "compact": true
    },
    "features": [
      {
        "key": "00000000-0000-4000-8000-000000000001",
        "record_uuid": "00000000-0000-4000-8000-000000000001",
        "lat": 4.6486,
        "lng": -74.0628,
        "label": "Point 1",
        "value": 47000
      },
      {
        "key": "00000000-0000-4000-8000-000000000002",
        "record_uuid": "00000000-0000-4000-8000-000000000002",
        "lat": 4.703,
        "lng": -74.03,
        "label": "Point 2",
        "value": 36000
      },
      {
        "key": "00000000-0000-4000-8000-000000000003",
        "record_uuid": "00000000-0000-4000-8000-000000000003",
        "lat": 4.7413,
        "lng": -74.0836,
        "label": "Point 3",
        "value": 28000
      },
      {
        "key": "00000000-0000-4000-8000-000000000004",
        "record_uuid": "00000000-0000-4000-8000-000000000004",
        "lat": 4.628,
        "lng": -74.146,
        "label": "Point 4",
        "value": 9000
      },
      {
        "key": "00000000-0000-4000-8000-000000000005",
        "record_uuid": "00000000-0000-4000-8000-000000000005",
        "lat": 4.676,
        "lng": -74.118,
        "label": "Point 5",
        "value": 14000
      }
    ]
  },
    "table": {
    "columns": [
      {
        "key": "col_1",
        "label": "Col 1",
        "data_type": "TEXT"
      },
      {
        "key": "col_2",
        "label": "Col 2",
        "data_type": "TEXT"
      },
      {
        "key": "col_3",
        "label": "Col 3",
        "data_type": "TEXT"
      },
      {
        "key": "status",
        "label": "Status",
        "data_type": "TEXT"
      },
      {
        "key": "col_5",
        "label": "Col 5",
        "data_type": "TIMESTAMP",
        "format": {
          "style": "DATE"
        }
      },
      {
        "key": "col_6",
        "label": "Col 6",
        "data_type": "DECIMAL",
        "format": {
          "style": "INTEGER"
        }
      }
    ],
    "rows": [
      {
        "row_key": "00000000-0000-4000-8000-000000000001",
        "values": {
          "col_1": "Row 1",
          "col_2": "Value 1A",
          "col_3": "Value 1B",
          "status": "Status A",
          "col_5": "2026-10-08T09:10:00Z",
          "col_6": 2310,
          "statusTone": "warning"
        }
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000002",
        "values": {
          "col_1": "Row 2",
          "col_2": "Value 2A",
          "col_3": "Value 2B",
          "status": "Status B",
          "col_5": "2026-10-07T15:40:00Z",
          "col_6": 860,
          "statusTone": "info"
        }
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000003",
        "values": {
          "col_1": "Row 3",
          "col_2": "Value 3A",
          "col_3": "Value 3B",
          "status": "Status A",
          "col_5": "2026-10-07T11:05:00Z",
          "col_6": 1480,
          "statusTone": "warning"
        }
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000004",
        "values": {
          "col_1": "Row 4",
          "col_2": "Value 4A",
          "col_3": "Value 4B",
          "status": "Status C",
          "col_5": "2026-10-06T16:20:00Z",
          "col_6": 540,
          "statusTone": "success"
        }
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000005",
        "values": {
          "col_1": "Row 5",
          "col_2": "Value 5A",
          "col_3": "Value 5B",
          "status": "Status D",
          "col_5": "2026-10-06T08:00:00Z",
          "col_6": 1120,
          "statusTone": "neutral"
        }
      }
    ]
  },
    "summary_table": {
    "columns": [
      {
        "key": "dim",
        "label": "Dimension",
        "role": "DIMENSION"
      },
      {
        "key": "m1",
        "label": "Measure 1",
        "role": "MEASURE",
        "format": {
          "style": "INTEGER"
        }
      },
      {
        "key": "m2",
        "label": "Measure 2",
        "role": "MEASURE",
        "format": {
          "style": "DECIMAL",
          "decimals": 1
        }
      },
      {
        "key": "m3",
        "label": "Measure 3",
        "role": "MEASURE",
        "format": {
          "style": "INTEGER"
        }
      }
    ],
    "rows": [
      {
        "values": {
          "dim": "Row 1",
          "m1": 58,
          "m2": 162.5,
          "m3": 21450
        }
      },
      {
        "values": {
          "dim": "Row 2",
          "m1": 51,
          "m2": 148.0,
          "m3": 19320
        }
      },
      {
        "values": {
          "dim": "Row 3",
          "m1": 47,
          "m2": 139.5,
          "m3": 17880
        }
      },
      {
        "values": {
          "dim": "Row 4",
          "m1": 44,
          "m2": 121.0,
          "m3": 15240
        }
      },
      {
        "values": {
          "dim": "Others",
          "m1": 212,
          "m2": 590.5,
          "m3": 52110
        },
        "is_others": true
      }
    ],
    "totals": {
      "dim": null,
      "m1": 412,
      "m2": 1161.5,
      "m3": 126000
    },
    "sort": {
      "field_key": "m3",
      "direction": "DESC"
    },
    "period": {
      "grain": "MONTH",
      "start": "2026-10-01",
      "end": "2026-10-31",
      "label": "Period label"
    }
  },
    "list": {
    "items": [
      {
        "row_key": "00000000-0000-4000-8000-000000000001",
        "slots": {
          "identity": [
            {
              "key": "id_1",
              "label": "Label 1",
              "value": "Item 1"
            },
            {
              "key": "id_2",
              "label": "Label 2",
              "value": "Value 1"
            }
          ],
          "status": [
            {
              "key": "state",
              "label": "Status",
              "value": "Status A"
            }
          ],
          "fact": [
            {
              "key": "fact_1",
              "label": "Fact 1",
              "value": 48210,
              "format": {
                "style": "INTEGER"
              }
            },
            {
              "key": "fact_2",
              "label": "Fact 2",
              "value": "2026-08-14",
              "format": {
                "style": "DATE"
              }
            }
          ],
          "signal": [
            {
              "key": "signal_1",
              "label": "Signal",
              "value": 1
            }
          ],
          "context": [
            {
              "key": "context_1",
              "label": "Context",
              "value": "Context 1"
            }
          ]
        }
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000002",
        "slots": {
          "identity": [
            {
              "key": "id_1",
              "label": "Label 1",
              "value": "Item 2"
            },
            {
              "key": "id_2",
              "label": "Label 2",
              "value": "Value 2"
            }
          ],
          "status": [
            {
              "key": "state",
              "label": "Status",
              "value": "Status A"
            }
          ],
          "fact": [
            {
              "key": "fact_1",
              "label": "Fact 1",
              "value": 91830,
              "format": {
                "style": "INTEGER"
              }
            },
            {
              "key": "fact_2",
              "label": "Fact 2",
              "value": "2026-10-06",
              "format": {
                "style": "DATE"
              }
            }
          ],
          "signal": [
            {
              "key": "signal_1",
              "label": "Signal",
              "value": 0
            }
          ],
          "context": [
            {
              "key": "context_1",
              "label": "Context",
              "value": "Context 1"
            }
          ]
        }
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000003",
        "slots": {
          "identity": [
            {
              "key": "id_1",
              "label": "Label 1",
              "value": "Item 3"
            },
            {
              "key": "id_2",
              "label": "Label 2",
              "value": "Value 3"
            }
          ],
          "status": [
            {
              "key": "state",
              "label": "Status",
              "value": "Status B"
            }
          ],
          "fact": [
            {
              "key": "fact_1",
              "label": "Fact 1",
              "value": 164500,
              "format": {
                "style": "INTEGER"
              }
            },
            {
              "key": "fact_2",
              "label": "Fact 2",
              "value": "2026-10-08",
              "format": {
                "style": "DATE"
              }
            }
          ],
          "signal": [
            {
              "key": "signal_1",
              "label": "Signal",
              "value": 2
            }
          ],
          "context": [
            {
              "key": "context_1",
              "label": "Context",
              "value": "Context 2"
            }
          ]
        }
      }
    ]
  },
    "profile_card": {
    "row_key": "00000000-0000-4000-8000-000000000001",
    "avatar_url": null,
    "slots": {
      "identity": [
        {
          "key": "id_1",
          "label": "Label 1",
          "value": "Record 1"
        },
        {
          "key": "id_2",
          "label": "Label 2",
          "value": "Value 1"
        }
      ],
      "status": [
        {
          "key": "status",
          "label": "Status",
          "value": "Status A"
        }
      ],
      "fact": [
        {
          "key": "fact_1",
          "label": "Fact 1",
          "value": "2021-03-02",
          "format": {
            "style": "DATE"
          }
        },
        {
          "key": "fact_2",
          "label": "Fact 2",
          "value": 12,
          "format": {
            "style": "INTEGER"
          }
        },
        {
          "key": "fact_3",
          "label": "Fact 3",
          "value": 184300,
          "format": {
            "style": "INTEGER"
          }
        },
        {
          "key": "fact_4",
          "label": "Fact 4",
          "value": "2026-10-07",
          "format": {
            "style": "DATE"
          }
        }
      ],
      "signal": [
        {
          "key": "signal_1",
          "label": "Signal",
          "value": 2310.5,
          "format": {
            "style": "DECIMAL",
            "decimals": 2
          }
        }
      ],
      "context": [
        {
          "key": "context_1",
          "label": "Context 1",
          "value": "Value 2"
        },
        {
          "key": "context_2",
          "label": "Context 2",
          "value": "Value 3"
        }
      ]
    }
  },
    "connections": {
    "groups": [
      {
        "key": "group_1",
        "label": "Group 1",
        "count": 12,
        "items": [
          {
            "record_uuid": "00000000-0000-4000-8000-000000000001",
            "label": "Item 1",
            "composite_code": "TYPE_1",
            "relationship_label": "Group 1",
            "since": "2021-03-02",
            "meta": {
              "role": "Role A"
            }
          },
          {
            "record_uuid": "00000000-0000-4000-8000-000000000002",
            "label": "Item 2",
            "composite_code": "TYPE_1",
            "relationship_label": "Group 1",
            "since": "2022-07-18",
            "meta": {
              "role": "Role A"
            }
          },
          {
            "record_uuid": "00000000-0000-4000-8000-000000000003",
            "label": "Item 3",
            "composite_code": "TYPE_1",
            "relationship_label": "Group 1",
            "since": "2024-01-09",
            "meta": {
              "role": "Role B"
            }
          }
        ]
      },
      {
        "key": "group_2",
        "label": "Group 2",
        "count": 2,
        "items": [
          {
            "record_uuid": "00000000-0000-4000-8000-000000000004",
            "label": "Item 4",
            "composite_code": "TYPE_2",
            "relationship_label": "Group 2",
            "since": "2021-03-02",
            "meta": {
              "role": "Role C"
            }
          },
          {
            "record_uuid": "00000000-0000-4000-8000-000000000005",
            "label": "Item 5",
            "composite_code": "TYPE_2",
            "relationship_label": "Group 2",
            "since": "2023-05-11",
            "meta": {
              "role": "Role D"
            }
          }
        ]
      }
    ]
  },
    "carousel": {
    "items": [
      {
        "row_key": "00000000-0000-4000-8000-000000000001",
        "title": "Item 1",
        "subtitle": "Subtitle 1",
        "badge": "Badge 1",
        "image_url": null
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000002",
        "title": "Item 2",
        "subtitle": "Subtitle 2",
        "badge": "Badge 2",
        "image_url": null
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000003",
        "title": "Item 3",
        "subtitle": "Subtitle 3",
        "badge": null,
        "image_url": null
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000004",
        "title": "Item 4",
        "subtitle": "Subtitle 4",
        "badge": "Badge 4",
        "image_url": null
      }
    ]
  },
    "board": {
    "lanes": [
      {
        "key": "LANE_1",
        "label": "Lane 1",
        "count": 6,
        "cards": [
          {
            "row_key": "00000000-0000-4000-8000-000000000001",
            "title": "Item 1",
            "subtitle": "Subtitle 1",
            "fields": {
              "field_1": "Value 1A",
              "field_2": "Value 1B"
            }
          },
          {
            "row_key": "00000000-0000-4000-8000-000000000002",
            "title": "Item 2",
            "subtitle": "Subtitle 2",
            "fields": {
              "field_1": "Value 2A",
              "field_2": "Value 2B"
            }
          }
        ]
      },
      {
        "key": "LANE_2",
        "label": "Lane 2",
        "count": 9,
        "cards": [
          {
            "row_key": "00000000-0000-4000-8000-000000000003",
            "title": "Item 3",
            "subtitle": "Subtitle 3",
            "fields": {
              "field_1": "Value 3A",
              "field_2": "Value 3B"
            }
          },
          {
            "row_key": "00000000-0000-4000-8000-000000000004",
            "title": "Item 4",
            "subtitle": "Subtitle 4",
            "fields": {
              "field_1": "Value 4A",
              "field_2": "Value 4B"
            }
          }
        ]
      },
      {
        "key": "LANE_3",
        "label": "Lane 3",
        "count": 1,
        "cards": [
          {
            "row_key": "00000000-0000-4000-8000-000000000005",
            "title": "Item 5",
            "subtitle": "Subtitle 5",
            "fields": {
              "field_1": "Value 5A",
              "field_2": "Value 5B"
            }
          }
        ]
      },
      {
        "key": "LANE_4",
        "label": "Lane 4",
        "count": 0,
        "cards": []
      }
    ]
  },
    "activity": {
    "events": [
      {
        "row_key": "00000000-0000-4000-8000-000000000001",
        "occurred_at": "2026-10-07T13:05:00Z",
        "title": "Event 1",
        "description": "Description 1",
        "actor": "Actor 1",
        "type": "CALL"
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000002",
        "occurred_at": "2026-10-07T10:20:00Z",
        "title": "Event 2",
        "description": null,
        "actor": "Actor 2",
        "type": "EMAIL"
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000003",
        "occurred_at": "2026-10-06T16:45:00Z",
        "title": "Event 3",
        "description": "Description 3",
        "actor": null,
        "type": "SMS"
      }
    ]
  },
    "alerts": {
    "alerts": [
      {
        "row_key": "00000000-0000-4000-8000-000000000001",
        "title": "Alert 1",
        "severity": "DANGER",
        "status": "OPEN",
        "message": "Message 1",
        "created_at": "2026-10-08T07:00:00Z"
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000002",
        "title": "Alert 2",
        "severity": "WARNING",
        "status": "OPEN",
        "message": "Message 2",
        "created_at": "2026-10-07T18:00:00Z"
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000003",
        "title": "Alert 3",
        "severity": "WARNING",
        "status": "ACK",
        "message": null,
        "created_at": "2026-10-07T12:30:00Z"
      },
      {
        "row_key": "00000000-0000-4000-8000-000000000004",
        "title": "Alert 4",
        "severity": "INFO",
        "status": "OPEN",
        "message": null,
        "created_at": "2026-10-07T19:05:00Z"
      }
    ],
    "counts_by_severity": {
      "INFO": 5,
      "SUCCESS": 0,
      "WARNING": 2,
      "DANGER": 1
    }
  },
    "ai_summary": {
    "resolution": "GENERATED_CONTENT",
    "summary_text": "Summary text. One paragraph of the agent's read, long enough to show how the card wraps and where the findings begin.",
    "highlights": [
      {
        "text": "Finding 1",
        "severity": "WARNING",
        "field_key": null
      },
      {
        "text": "Finding 2",
        "severity": "DANGER",
        "field_key": "field_2"
      },
      {
        "text": "Finding 3",
        "severity": "SUCCESS",
        "field_key": null
      }
    ],
    "sources": [
      {
        "field_key": "field_1",
        "record_uuid": "00000000-0000-4000-8000-000000000001"
      },
      {
        "field_key": null,
        "record_uuid": "00000000-0000-4000-8000-000000000002"
      }
    ],
    "query_ref": null,
    "context_refs": [
      {
        "ref": "query_1",
        "query_definition_uuid": "00000000-0000-4000-8000-000000000011",
        "build_hash": "hash_1",
        "parameters": {
          "CURRENT_RECORD_UUID": "00000000-0000-4000-8000-000000000001"
        },
        "row_count": 27
      },
      {
        "ref": "query_2",
        "query_definition_uuid": "00000000-0000-4000-8000-000000000012",
        "build_hash": "hash_2",
        "parameters": {
          "CURRENT_RECORD_UUID": "00000000-0000-4000-8000-000000000001"
        },
        "row_count": 64
      }
    ],
    "generated_at": "2026-10-08T14:58:00Z",
    "provider_ref": "agent-run-1"
  },
    "next_best_action": {
    "actions": [
      {
        "action_code": "ACTION_1",
        "title": "Action 1",
        "reason": "Reason 1",
        "score": 0.92,
        "priority": "HIGH",
        "due_date": "2026-10-15",
        "row_key": "00000000-0000-4000-8000-000000000001",
        "available": true
      },
      {
        "action_code": "ACTION_2",
        "title": "Action 2",
        "reason": "Reason 2",
        "score": 0.81,
        "priority": "MEDIUM",
        "due_date": "2026-10-10",
        "row_key": "00000000-0000-4000-8000-000000000002",
        "available": true
      },
      {
        "action_code": "ACTION_3",
        "title": "Action 3",
        "reason": "Reason 3",
        "score": 0.64,
        "priority": "LOW",
        "due_date": null,
        "row_key": "00000000-0000-4000-8000-000000000003",
        "available": false
      }
    ],
    "context_refs": []
  }
  }

/**
 * The ids that name a fixture someone else already owns.
 *
 * Eight of them are the renderer ids the backend spells differently from the
 * drawing's name. The ninth, `chart`, is the catalogue's coarse name for every
 * chart — it resolves to the line chart, which is the one the registry draws
 * for it.
 */
const SAMPLE_ALIASES = Object.freeze({
  stat_row: 'metric_row',
  gauge: 'indicator',
  heat_map: 'heatmap',
  pie: 'pie_chart',
  area: 'area_chart',
  feed: 'activity',
  usage_heatmap: 'usage_map',
  composite_stat: 'composite_metric',
  chart: 'line_chart',
});

/**
 * Every fixture, under both spellings.
 *
 * The aliases are folded into the object rather than resolved only in
 * `sampleFor`, because `CATALOG_WIDGET_SAMPLES.gauge` is how Storybook and the
 * library already reach for them — and a lookup that silently returns
 * `undefined` renders an empty card instead of failing, which is the kind of
 * gap nobody finds until a reviewer asks why the gauge story is blank.
 */
export const CATALOG_WIDGET_SAMPLES = Object.freeze({
  ...SAMPLES_BY_TYPE,
  ...Object.fromEntries(
    Object.entries(SAMPLE_ALIASES).map(([alias, target]) => [alias, SAMPLES_BY_TYPE[target]]),
  ),
});

/**
 * Sample data for a type.
 *
 * Ids arrive in two cases — the builder's lowercase ones and the backend's
 * upper-snake `typeCode` — and under two names for those eight, so both are
 * folded here rather than at every call site.
 */
export const sampleFor = (typeId) => CATALOG_WIDGET_SAMPLES[String(typeId ?? '').toLowerCase()] ?? null;

export default CATALOG_WIDGET_SAMPLES;
